import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ic_plus from "../../../assets/images/icons/plus.svg";
import cancel from "../../../assets/images/icons/x-black.svg";
import {
  GeneralButton,
  GeneralCheckbox,
  Modal,
  SwitchRadioButton,
} from "../../../components";
import { IScheduleItem } from "../../../interface/GeneralResponse";
import { formatTime24To12 } from "../../../utils/utils";
import TimeToggle from "./TimeToggle";

interface SingleDayProps {
  Id: number;
  validationNumber?: number;
  Name: string;
  DisplayName: string;
  isActive: boolean;
  dayData: {
    shifts: IScheduleItem[];
    isActive: boolean;
  };
  setDayActive: (DayId: number, isActive: boolean) => void;
  addShiftToAllDays: (Id: number, newShift: IScheduleItem) => any;
  addShift: (DayId: number, newShift: IScheduleItem) => void;
  removeShift: (DayId: number, index: number) => void;
  showError?: boolean;
}

const SingleDay: React.FC<SingleDayProps> = ({
  Id,
  DisplayName,
  dayData,
  setDayActive,
  addShift,
  removeShift,
  validationNumber = 15,
  showError,
  addShiftToAllDays,
}) => {
  const defaultShift: IScheduleItem = {
    DayId: Id,
    IsActive: true,
    From: "",
    To: "",
  };

  const { t } = useTranslation();

  const [newShift, setNewShift] = useState<IScheduleItem>(defaultShift);
  const [addToAllDays, setAddToAllDays] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleRemoveShift = (index: number) => {
    removeShift(Id, index);
  };
  const handleCloseModal = () => {
    setError(null);
      setNewShift({
        DayId: Id,
        IsActive: true,
        From: "",
        To: "",
      });
      setAddToAllDays(false);
    setOpenModal(false);
  };
  const toggleDayActive = () => {
    setDayActive(Id, !dayData.isActive);
    if (!dayData.isActive && dayData.shifts.length === 0) {
      setNewShift({
        DayId: Id,
        IsActive: true,
        From: "09:00",
        To: "17:00",
      });
    }
  };

  const handleAddShift = () => {
    if (!newShift.From || !newShift.To) {
      setError(t("both_times_required"));
      return;
    }

    const start = moment(newShift.From, "HH:mm");
    const end = moment(newShift.To, "HH:mm");

    if (start.isSameOrAfter(end)) {
      setError(t("start_time_before_end_time"));
      return;
    }

    if (end.diff(start, "minutes") < validationNumber) {
      setError(
        `${t("schedule_error_msg", {
          minutes: validationNumber,
        })} ${validationNumber} ${t("minutes")}`,
      );
      return;
    }

    if (addToAllDays) {
      const conflictError = addShiftToAllDays(Id, newShift);

      if (conflictError) {
        setError(conflictError);
        return;
      }

      handleCloseModal();
      setNewShift(defaultShift);
      setAddToAllDays(false);
      return;
    }

    const hasConflictInCurrentDay = dayData.shifts.some(
      (shift: IScheduleItem) =>
        moment(shift.From, "HH:mm").isBefore(end) &&
        moment(shift.To, "HH:mm").isAfter(start),
    );

    if (hasConflictInCurrentDay) {
      setError(t("this_time_includes_this_day"));
      return;
    }

    addShift(Id, newShift);

    handleCloseModal();
    setNewShift(defaultShift);
  };

  const [isHintOpen, setIsHintOpen] = useState<boolean>(false);

  useEffect(() => {
    if (isHintOpen) {
      setIsHintOpen(false);
    }
  }, [isHintOpen, showError]);

  useEffect(() => {
    setIsHintOpen(
      !!showError && !!dayData.isActive && dayData.shifts.length === 0,
    );
  }, [showError, dayData]);
  return (
    <div className="w-full p-2 max-sm:p-0">
      <div className="flex items-center justify-between">
        <SwitchRadioButton
          checked={dayData.isActive}
          onChange={toggleDayActive}
          text={DisplayName}
        />
      </div>
      {showError && dayData.isActive && dayData.shifts.length === 0 && (
        <p className="mt-2 text-center text-sm text-error">
          {t("please_add_at_least_one_shift")}
        </p>
      )}

      <div
        className={`mt-1 transition-all ${
          dayData.isActive ? "max-h-full opacity-100" : "max-h-0 opacity-0"
        } overflow-hidden`}
      >
        {dayData.shifts.map((shift, index) => (
          <div
            key={`${Id}-${shift.From}-${shift.To}`}
            className="my-2 flex flex-wrap items-center gap-3 text-body-text max-sm:gap-1"
          >
            {formatTime24To12(shift.From)}
            {" - "}
            {formatTime24To12(shift.To)}
            <img
              src={cancel}
              alt="cancel"
              className="w-4 cursor-pointer"
              onClick={() => handleRemoveShift(index)}
            />
          </div>
        ))}
        <button
          className="flex items-center gap-1 text-sm font-bold text-primary"
          onClick={() => setOpenModal(true)}
        >
          <img src={ic_plus} alt="add" className="w-4" />
          {t("add_shift")}
        </button>
      </div>
      <Modal
        header={DisplayName + " " + t("working_times")}
        isOpen={openModal}
        onClose={handleCloseModal}
      >
        <div className="flex flex-col gap-3">
          <TimeToggle
            value={newShift.From}
            onChange={(value: any) =>
              setNewShift((prev) => ({
                ...prev,
                From: value,
              }))
            }
            placeholder={t("start_time")}
          />
          <TimeToggle
            value={newShift.To}
            onChange={(value: any) =>
              setNewShift((prev) => ({
                ...prev,
                To: value,
              }))
            }
            placeholder={t("end_time")}
          />
          <GeneralCheckbox
            inputClasses="appointment-checkbox !w-6 !h-6"
            name={"allDays"}
            onChange={(event) => {
              setAddToAllDays(event.target.checked);
            }}
            label={t("default_for_all")}
          />
          {error && <p className="mt-2 text-sm text-error">{error}</p>}
          <div className="mt-4 flex items-center justify-end gap-2">
            <GeneralButton
              title={t("cancel")}
              color="liteGray"
              onClick={handleCloseModal}
            />
            <GeneralButton
              className="!px-5"
              title={t("submit")}
              onClick={handleAddShift}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default React.memo(SingleDay);
